import { Component, computed, OnDestroy, Signal, inject } from '@angular/core';
import { FormBuilder, FormGroup, ValidationErrors, ValidatorFn, Validators } from '@angular/forms';
import { Subject, throwError } from 'rxjs';
import { catchError, finalize, takeUntil, tap } from 'rxjs/operators';
import { PlaygroundService, EndpointCatalogService, NotificationService } from '../../../core/services';
import { EndpointDefinition, getEndpointDefinition } from '../../../core/models';

import { PagesResult } from '../models/pages-result.model';
import { buildCodeSnippets, CodeLanguage } from '../utils/code-snippet';
import { estimateEndpointPrice, PriceEstimate } from '../utils/endpoint-pricing';
import { PlaygroundQueryPage } from '../utils/playground-query-page';
import { FieldMeta, buildFieldsGroup, flattenFields } from '../utils/result-fields';
import { PlaygroundKeyService } from '../utils/playground-key.service';

export type PagesMode = 'single' | 'subsequent';

/** Requires at least one control in the group to be true matches the
 *  "At least one option must be selected" hint under Data fields per
 *  result, which was previously just copy with nothing enforcing it. */
function atLeastOneSelected(): ValidatorFn {
  return (group): ValidationErrors | null => {
    const anySelected = Object.values(group.value ?? {}).some(v => v === true);
    return anySelected ? null : { atLeastOneSelected: true };
  };
}

@Component({
  selector: 'app-pages',
  standalone: false,
  templateUrl: './pages.html',
  styleUrl: './pages.scss',
})
export class Pages implements OnDestroy, PlaygroundQueryPage {
  constructor(
    private fb: FormBuilder,
    private playgroundService: PlaygroundService,
    private catalogService: EndpointCatalogService,
    private notificationService: NotificationService,
  ) {
    this.form = this.buildForm();

    // /pages has no entry in the live catalog yet (still "Not ready"), so
    // this stays undefined and estimateEndpointPrice() returns a zero
    // estimate until the backend adds it see endpoint-catalog.model.ts.
    this.endpointDef = computed(() => getEndpointDefinition(this.catalogService.catalog(), 'pages'));

  }

  private readonly _destroy$ = new Subject<void>();

  readonly endpointDef: Signal<EndpointDefinition | undefined>;

  isLoading = false;
  status: 'idle' | 'ok' | 'error' = 'idle';
  response: PagesResult | null = null;
  latencyMs: number | null = null;
  credits: number | null = null;

  activeLanguage: CodeLanguage = 'ruby';
  copied = false;

  // ── Collapsible sections Response starts collapsed, it's set once and
  // rarely revisited; the other two are what changes fetch to fetch. ──
  sectionOpen: Record<'target' | 'access' | 'response', boolean> = {
    target: true,
    access: true,
    response: false,
  };

  toggleSection(key: keyof Pages['sectionOpen']): void {
    this.sectionOpen[key] = !this.sectionOpen[key];
  }

  /** The key runs bill to: picked once in the run bar (playground.html). */
  private readonly apiKey = inject(PlaygroundKeyService);

  readonly FIELD_META: FieldMeta[] = [
    { key: 'url',                 wireKey: 'url',                    label: 'URL', required: true },
    { key: 'description',         wireKey: 'description',            label: 'Description', defaultOn: true },
    { key: 'content',             wireKey: 'content',                label: 'Content' },
    { key: 'publicationDate',     wireKey: 'publication_date',       label: 'Publication Date' },
    { key: 'linksInMainContent',  wireKey: 'links_in_main_content',  label: 'Links in main content' },
    { key: 'domainName',          wireKey: 'domain_name',            label: 'Domain Name' },
    { key: 'title',               wireKey: 'title',                  label: 'Title', defaultOn: true },
    { key: 'author',              wireKey: 'author',                 label: 'Author' },
    { key: 'mainImageUrl',        wireKey: 'main_image_url',         label: 'Main Image URL' },
    { key: 'indexationDate',      wireKey: 'indexation_date',        label: 'Indexation Date' },
    { key: 'documentLinks',       wireKey: 'document_links',         label: 'Document links' },
    { key: 'favicon',             wireKey: 'favicon',                label: 'Favicon' },
  ];

  form: FormGroup;

  private buildForm(): FormGroup {
    return this.fb.group({
      mode: ['single' as PagesMode],
      url: ['', Validators.required],
      residentialIp: [false],
      ipFromUsa: [false],
      bypassRobotsTxt: [false],
      closePopups: [false],
      headlessBrowser: [false],
      useCikisiIndex: [true],
      fields: buildFieldsGroup(this.fb, this.FIELD_META, { validators: atLeastOneSelected() }),
    });
  }

  get fieldsGroup(): FormGroup {
    return this.form.get('fields') as FormGroup;
  }

  setMode(mode: PagesMode): void {
    this.form.get('mode')!.setValue(mode);
  }

  // ── Pricing ──────────────────────────────────────────────────────────
  /** Live price estimate, recomputed from current form state on every
   *  change against the real catalog see utils/endpoint-pricing.ts. No
   *  confirmed surcharge_cents params for /pages yet, so nothing's marked
   *  active until the catalog describes one. */
  get estimatedPrice(): PriceEstimate {
    return estimateEndpointPrice(this.endpointDef(), []);
  }

  get requestSummary(): string {
    return (this.form.value.url ?? '').trim();
  }

  get codeSnippet(): string {
    return buildCodeSnippets('pages', 'pages', this.buildPayload())[this.activeLanguage];
  }

  setLanguage(lang: CodeLanguage): void {
    this.activeLanguage = lang;
  }

  copySnippet(): void {
    navigator.clipboard?.writeText(this.codeSnippet).then(() => {
      this.copied = true;
      setTimeout(() => { this.copied = false; }, 2000);
    });
  }

  private buildPayload() {
    const v = this.form.getRawValue();

    // One boolean per field (include_url, include_description, ...) instead
    // of an array of the selected names matches how /rag's own response
    // fields (include_relevance_score, include_article_title, ...) already work.
    const fields = flattenFields(this.FIELD_META, v.fields);

    // Nested the same way the UI is: `access` = the "Access configuration"
    // card (named for what it actually is here, not forced into
    // "request" there's no search/filter concept on this endpoint),
    // `response` = the "Response configuration" card. `url`/`mode` sit
    // inside `access`, same reasoning as `question` sitting inside
    // `request` on the search-style endpoints.
    return {
      access: {
        url: v.url,
        mode: v.mode,
        residential_ip: v.residentialIp,
        ip_from_usa: v.ipFromUsa,
        bypass_robots_txt: v.bypassRobotsTxt,
        close_popups: v.closePopups,
        headless_browser: v.headlessBrowser,
        use_cikisi_index: v.useCikisiIndex,
      },
      response: {
        fields,
      },
    };
  }

  runQuery(): void {
    if (this.form.invalid) {
      this.form.markAllAsTouched();
      const message = this.fieldsGroup.invalid
        ? 'Select at least one data field before fetching.'
        : 'Enter a webpage URL before fetching.';
      this.notificationService.error(message);
      return;
    }

    // The Run button is disabled for this already (see playground.html) this is
    // just a backstop, since the backend can only 422 without a key to bill.
    if (!this.apiKey.keys().length) {
      this.notificationService.error('Create an API key before running this query.');
      return;
    }

    const payload = this.buildPayload();

    this.isLoading = true;
    this.playgroundService.runPages(payload, this.apiKey.selectedId() ?? undefined)
      .pipe(
        takeUntil(this._destroy$),
        catchError(error => {
          this.status = 'error';
          this.notificationService.error('An error occurred while running the query. Please try again later.');
          return throwError(error);
        }),
        tap(({ result, latencyMs, credits }) => {
          this.response = result;
          this.latencyMs = latencyMs;
          this.credits = credits;
          this.status = 'ok';
        }),
        finalize(() => { this.isLoading = false; }),
      ).subscribe();
  }

  /** Called from the shared Playground footer's Reset button (see
   *  playground.ts/.html) once the user confirms the confirmation
   *  prompt itself lives there, not here, since it's one shared footer
   *  for all seven playground pages. */
  resetForm(): void {
    this.form = this.buildForm();
    this.status = 'idle';
    this.response = null;
    this.latencyMs = null;
    this.credits = null;
  }

  ngOnDestroy(): void {
    this._destroy$.next();
    this._destroy$.complete();
  }
}
